import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  ChakraProps,
  Flex,
  Heading,
  Icon,
  IconButton,
  Image,
} from "@chakra-ui/react";
import { useAuthentication } from "hooks/useAuthentication";
import { usePermissions } from "hooks/usePermissions";
import { useUserRole } from "hooks/useUserRole";
import React from "react";
import {
  MdAttachMoney,
  MdCollections,
  MdDocumentScanner,
  MdEditDocument,
  MdFileDownload,
  MdLocationOn,
  MdLockPerson,
  MdLogout,
  MdPayments,
  MdQrCode,
  MdWorkspaces,
} from "react-icons/md";
import { Menu, MenuItem, Sidebar, SubMenu } from "react-pro-sidebar";
import { pageRoles } from "utils/roles";

/** 26 entrées du sous-menu Tracking (MAJ_menus.md) : label, route, permission get <model> */
const TRACKING_SUBMENU_ITEMS: { label: string; link: string; permission: string }[] = [
  { label: "Conducteurs", link: "/tracking/drivers", permission: "get driver" },
  { label: "Parents", link: "/tracking/parents", permission: "get parent" },
  { label: "Élèves", link: "/tracking/students", permission: "get eleve" },
  { label: "Adresses", link: "/tracking/addresses", permission: "get address" },
  { label: "Trajets", link: "/tracking/trips", permission: "get trip" },
  { label: "Arrêts", link: "/tracking/stops", permission: "get stop" },
  { label: "Scan des élèves", link: "/tracking/trip-students-can", permission: "get tripstudentscan" },
  { label: "Stops des trajets", link: "/tracking/trip-stops", permission: "get tripstop" },
  { label: "Positions des trajets", link: "/tracking/trip-positions", permission: "get tripposition" },
  { label: "Présence des élèves", link: "/tracking/student-presence", permission: "get student_presence" },
  { label: "Historique indisponibilité", link: "/tracking/student-unavailability-history", permission: "get student_unavailability_history" },
  { label: "Paiements", link: "/tracking/payments", permission: "get payment" },
  { label: "Routes", link: "/tracking/routes", permission: "get route" },
  { label: "Bus", link: "/tracking/bus", permission: "get bus" },
  { label: "Notifications", link: "/tracking/notifications", permission: "get notification" },
  { label: "Patrouilles", link: "/tracking/patrols", permission: "get patrol" },
  { label: "Media des incidents", link: "/tracking/incident-media", permission: "get incidentmedia" },
  { label: "Configurations SLA", link: "/tracking/sla-config", permission: "get slaconfig" },
  { label: "Gardes", link: "/tracking/guard-agents", permission: "get guardagent" },
  { label: "Shifts", link: "/tracking/guard/shifts", permission: "get guardshift" },
  { label: "Points de contrôle", link: "/tracking/guard-posts", permission: "get guardpost" },
  { label: "Sociétés de gardiennage", link: "/tracking/guard-companies", permission: "get guardcompany" },
  { label: "Sites de gardiennage", link: "/tracking/guard/sites", permission: "get guardedsite" },
  { label: "Checks", link: "/tracking/patrol-checks", permission: "get patrolcheck" },
  { label: "Types d'incidents", link: "/tracking/incident-types", permission: "get incidenttype" },
  { label: "Incidents", link: "/tracking/guard/incidents", permission: "get incident" },
];

const menuItems = [
  {
    label: "Tableau de bord Tax",
    link: "/tax/dashboard",
    icon: (
      <Image
        src="images/img_dashboard_svgrepo_com.svg"
        alt="Dashboard Icon"
        h="16px"
        w="16px"
      />
    ),
    roles: [...pageRoles.taxSuperAdmin, ...pageRoles.taxAdmin, ...pageRoles.taxPayer, ...pageRoles.taxCollector, ...pageRoles.taxSupervisor, ...pageRoles.dealer],
    model: "taxdashboard",
  },
  {
    label: "Tableau de bord",
    link: "/dashboard",
    icon: (
      <Image
        src="images/img_dashboard_svgrepo_com.svg"
        alt="Dashboard Icon"
        h="16px"
        w="16px"
      />
    ),
    roles: [...pageRoles.superUser, ...pageRoles.company],
    model: "dashboard",
  },
  // Tracking : rendu via SubMenu (voir plus bas), pas comme MenuItem simple
  {
    label: "Tracking",
    link: "/tracking",
    icon: <Icon as={MdLocationOn as any} boxSize={4} />,
    roles: [...pageRoles.superUser, ...pageRoles.company],
    model: "tracking_dashboard",
  },
  {
    label: "Clients",
    link: "/clients",
    icon: (
      <Image
        src="images/img_handshake_deal_svgrepo_com.svg"
        alt="Clients Icon"
        h="16px"
        w="16px"
      />
    ),
    roles: pageRoles.superUser,
    model: "company",
  },
  {
    label: "Produits",
    link: "/products",
    icon: (
      <Image
        src="images/img_filter.svg"
        alt="Products Icon"
        h="16px"
        w="16px"
      />
    ),
    roles: pageRoles.superUser,
    model: "product",
  },
  {
    label: "Documents",
    link: "/documents",
    icon: <Icon as={MdEditDocument as any} boxSize={4} />,
    roles: pageRoles.superUser,
    model: "member",
  },
  {
    label: "Dealers",
    link: "/dealers",
    icon: (
      <Image src="images/dealer_icon.svg" alt="Dealer Icon" h="16px" w="16px" />
    ),
    roles: pageRoles.superUser,
    model: "dealer",
  },
  {
    label: "Utilisateurs",
    link: "/users",
    icon: (
      <Image src="images/img_lock.svg" alt="Users Icon" h="16px" w="16px" />
    ),
    roles: pageRoles.superUser,
    model: "systemuser",
  },
  {
    label: "Codes QR",
    link: "/qrcode-requests",
    icon: <Icon as={MdQrCode as any} boxSize={4} />,
    roles: pageRoles.superUser,
    model: "qrcodes",
  },
  {
    label: "Scans",
    link: "/logs",
    icon: <Icon as={MdDocumentScanner as any} boxSize={4} />,
    roles: pageRoles.superUser,
    model: "log",
  },
  {
    label: "Signalements",
    link: "/complaints",
    icon: (
      <Image
        src="images/img_exclamation_cir.svg"
        alt="Reports Icon"
        h="16px"
        w="16px"
      />
    ),
    roles: pageRoles.superUser,
    model: "complaint",
  },
  {
    label: "Institutions",
    link: "/institutions",
    icon: <Icon as={MdWorkspaces as any} boxSize={4} />,
    roles: pageRoles.taxSuperAdmin,
    model: "institution",
  },
  {
    label: "Imputation de créances",
    link: "/tax-assessments",
    icon: <Icon as={MdAttachMoney as any} boxSize={4} />,
    roles: pageRoles.taxAdmin,
    model: "taxpayer",
  },
  {
    label: "Débiteurs",
    link: "/tax-payers",
    icon: (
      <Image
        src="images/tax_payer.png"
        alt="Tax Payer Icon"
        h="16px"
        w="16px"
      />
    ),
    roles: pageRoles.taxAdmin,
    model: "taxpayer",
  },
  {
    label: "Collecteurs",
    link: "/tax-collectors",
    icon: <Icon as={MdCollections as any} boxSize={4} />,
    roles: pageRoles.taxAdmin,
    model: "taxcollector",
  },
  {
    label: "Superviseurs",
    link: "/tax-supervisors",
    icon: <Icon as={MdLockPerson as any} boxSize={4} />,
    roles: pageRoles.taxAdmin,
    model: "taxsupervisor",
  },
  {
    label: "Fichiers",
    link: "/tax/data-uploads",
    icon: <Icon as={MdFileDownload as any} boxSize={4} />,
    roles: pageRoles.taxAdmin,
    model: "institution",
  },
  {
    label: "Scans",
    link: "/tax/logs",
    icon: <Icon as={MdDocumentScanner as any} boxSize={4} />,
    roles: pageRoles.taxAdmin,
    model: "taxlog",
  },
  {
    label: "Transactions",
    link: "/tax-payments",
    icon: <Icon as={MdPayments as any} boxSize={4} />,
    roles: pageRoles.taxAdmin,
    model: "taxpayment",
  },
  {
    label: "Paramètres",
    link: "/settings",
    icon: (
      <Image
        src="images/img_settings_svgrepo_com.svg"
        alt="Settings Icon"
        h="16px"
        w="16px"
      />
    ),
    roles: pageRoles.superUser,
    model: "setting",
  },
];

interface Props extends ChakraProps {
  className?: string;
}

export default function Sidebar3({ ...props }: Props) {
  const [collapsed, setCollapsed] = React.useState(true);
  const {
    authProvider: { signOut },
    hasPermission,
    user,
  } = useAuthentication();
  const { has: hasPermissionStr } = usePermissions();
  const userRole = useUserRole();

  // console.log(user);

  //use this function to collapse/expand the sidebar
  function collapseSidebar() {
    setCollapsed(!collapsed);
  }

  const handleLogout = async () => {
    await signOut();
  };

  return (
    <Box
      {...props}
      minW={"60px"}
      width="200px !important"
      collapsedWidth="60px !important"
      collapsed={collapsed}
      as={Sidebar}
      pt={{ base: "20px", sm: "24px" }}
      flexDirection="column"
      display="flex"
      bg="white"
      boxShadow="xs"
      h="100vh"
      top="0px"
      overflow="auto"
      px="5px"
      sx={{
        position: "sticky !important",
      }}
    >
      <Box
        position={"absolute"}
        top={0}
        right={3}
        mb={10}
        onClick={collapseSidebar}
      >
        <IconButton
          aria-label="Toggle Menu"
          fontSize="15px"
          icon={collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          borderWidth={"2px"}
          borderRadius={"20px"}
          borderColor={"gray_stroke.0"}
          borderStyle={"solid"}
          width={"30px"}
          height={"30px"}
          size={"sm"}
        />
      </Box>
      <Image
        src="images/logo.png"
        alt="Sidebar Image"
        h={collapsed ? "80px" : "100px"}
        mt="30px"
        w={collapsed ? "80px" : "100px"}
        fit="contain"
        borderRadius="24px"
      />
      <Box
        menuItemStyles={{
          button: {
            padding: "8px",
            backgroundColor: "#ffffff",
            color: "#5f6d7e",
            fontWeight: 400,
            fontSize: "14px",
          },
        }}
        rootStyles={{ "&>ul": { gap: "16px" } }}
        as={Menu}
        mt="10px"
        pr="6px"
        display="flex"
        alignSelf="stretch"
        flexDirection="column"
        w="100%"
      >
        {(() => {
          // Déterminer quel dashboard afficher selon le rôle de l'utilisateur
          // userRole : role à la racine ou dans user (un seul rôle à la fois)
          const taxDashboardRoles = [
            "taxsuperadmin",
            "taxadmin", 
            "taxcollector",
            "taxsupervisor",
            "dealer",
            "taxpayer", // debitor
            "debitor"
          ];
          
          // Si l'utilisateur a un rôle tax, afficher taxdashboard
          const shouldShowTaxDashboard = userRole && taxDashboardRoles.includes(userRole);
          
          // Trouver le menu dashboard approprié
          const dashboardMenu = menuItems.find((menu: any) => 
            shouldShowTaxDashboard 
              ? menu.model === "taxdashboard" 
              : menu.model === "dashboard"
          );
          
          // Afficher le dashboard si l'utilisateur a la permission
          if (dashboardMenu && hasPermission(dashboardMenu.model, "get")) {
            return (
              <MenuItem 
                href={dashboardMenu.link} 
                icon={dashboardMenu.icon} 
                key={`${dashboardMenu.label}-${dashboardMenu.model}`}
              >
                {dashboardMenu.label}
              </MenuItem>
            );
          }
          return null;
        })()}
        {/* Sous-menu Tracking : Dashboard + 26 entrées (MAJ_menus.md) */}
        {(hasPermissionStr("get tracking_dashboard") ||
          TRACKING_SUBMENU_ITEMS.some((item) => hasPermissionStr(item.permission))) && (
          <SubMenu
            label="Tracking"
            icon={<Icon as={MdLocationOn as any} boxSize={4} />}
            key="tracking-submenu"
          >
            {hasPermissionStr("get tracking_dashboard") && (
              <MenuItem href="/tracking" key="tracking-dashboard">
                Dashboard
              </MenuItem>
            )}
            {TRACKING_SUBMENU_ITEMS.map((item) =>
              hasPermissionStr(item.permission) ? (
                <MenuItem href={item.link} key={item.link}>
                  {item.label}
                </MenuItem>
              ) : null
            )}
          </SubMenu>
        )}
        {/* Afficher les autres menus selon les permissions */}
        {menuItems?.map(
          (menu: any) =>
            menu.model !== "dashboard" &&
            menu.model !== "taxdashboard" &&
            menu.model !== "tracking_dashboard" && // Tracking rendu via SubMenu ci-dessus
            menu.roles !== undefined &&
            hasPermission(menu.model, "get") && (
              <MenuItem href={menu.link} icon={menu.icon} key={`${menu.label}-${menu.model}`}>
                {menu.label}
              </MenuItem>
            )
        )}
        {menuItems?.map(
          (menu: any) =>
              menu.model === undefined && (
              <MenuItem href={menu.link} icon={menu.icon} key={`${menu.label}-undefined`}>
                {menu.label}
              </MenuItem>
            )
        )}
      </Box>
      {!collapsed ? (
        <Box mt="192px" alignSelf="stretch">
          <Flex flexDirection="column" alignItems="center">
            <Image
              src="images/img_logout_2_svgrepo_com.svg"
              alt="Logout Icon"
              h="20px"
              zIndex={1}
              w="20px"
              position="relative"
            />
            <Flex
              mt="-24px"
              gap="12px"
              position="relative"
              bg="red_bg"
              alignSelf="stretch"
              flexDirection="column"
              alignItems="center"
              px="18px"
              py={{ base: "20px", sm: "22px" }}
              borderRadius="8px"
            >
              <Heading size="headingsm" as="h6" fontFamily="Arimo">
                Origin360
              </Heading>
              <Button
                size="xl"
                colorScheme="red_origin"
                leftIcon={<Icon as={MdLogout as any} boxSize={4} />}
                fontFamily="Arimo"
                fontWeight={600}
                fontSize={"14px"}
                gap="4px"
                alignSelf="stretch"
                // mx="22px"
                borderRadius="8px"
                onClick={handleLogout}
              >
                Se déconnecter
              </Button>
            </Flex>
          </Flex>
        </Box>
      ) : null}
    </Box>
  );
}
